'use client';

import { useState } from 'react';
import { ShowHide } from '@zillow/constellation-10';
import { splitDescription } from './splitDescription';

type ReadMoreTextProps = {
  /** Full text content. Auto-split into truncated + hidden chunks. */
  content: string;
  /** Approximate truncation length (default: 150 chars). */
  maxTruncatedLength?: number;
  /** Button text when collapsed. */
  readMoreText: string;
  /** Button text when expanded. */
  readLessText: string;
  /** Optional aria-label for the toggle button. */
  'aria-label'?: string;
  /** Stable id for Constellation ShowHide Root / Button aria-controls. */
  controlsId: string;
};

/**
 * Domain replacement for the deleted `ReadMoreButton` HALO primitive.
 *
 * Composes Constellation `ShowHide.Root` + `ShowHide.Hidden` + a `renderButton`
 * with translated label text. Per Constellation's API, `ShowHide.Root` requires
 * a SINGLE wrapping element child whose inner children are the truncated
 * content + `ShowHide.Hidden`; the ellipsis and toggle button are injected
 * automatically when `appearance="inline"`. Don't pass `<ShowHide.Button>` as
 * a sibling — use `renderButton` to customize its label.
 */
export const ReadMoreText = ({
  content,
  maxTruncatedLength = 150,
  readMoreText,
  readLessText,
  'aria-label': ariaLabel,
  controlsId,
}: ReadMoreTextProps) => {
  const { truncated, hidden } = splitDescription(content, maxTruncatedLength);
  const [isHidden, setIsHidden] = useState(true);

  if (!hidden) {
    return <>{content}</>;
  }

  return (
    <ShowHide.Root
      id={controlsId}
      appearance="inline"
      isHidden={isHidden}
      onDisplayChange={() => setIsHidden((prev) => !prev)}
      renderButton={() => (
        <ShowHide.Button aria-controls={controlsId} aria-label={ariaLabel}>
          {isHidden ? readMoreText : readLessText}
        </ShowHide.Button>
      )}
    >
      <span>
        {truncated}
        <ShowHide.Hidden>{hidden}</ShowHide.Hidden>
      </span>
    </ShowHide.Root>
  );
};
